// Sight: what each player character sees, what each sight group has explored, and what a player is sent of the walls
// (issue 1144, slice 2; docs/VTT/design/30-sight-walls-and-light.md §1.6, §2, §3, §4).
//
// VISION is the DM's switch per scene, OFF by default: with it off nothing here runs and every frame is what it was.
// With it on, tokens follow sight (§10 Q4: the DM's manual reveal shows the map only), and a player's map picture is
// masked to their explored squares in tiles (§5; `tiles.ts`).
//
// SIGHT IS DERIVED, NEVER STORED (§2.2): a function of the state — the player characters' positions, the walls and
// doors, the grid and the map's size. It is kept in memory beside each state object (a WeakMap: nothing reaches a
// snapshot or the log) and worked out again only for the eyes a change affects (§2.1): `advanceSight(before, after)`
// carries every eye whose position and walls are unchanged across, so a move recomputes the mover, a wall or door or
// grid change everyone, and a character added, removed, hidden or shown only that character. The setting regroups and
// recomputes nothing. `projectEvent` advances it itself, so every caller (the server's commit, the tests, the DM's
// "view as") gets the incremental path; a state reached any other way is worked out whole.
//
// EXPLORED IS HISTORY, SO IT IS STORED (§3): per scene, in the fog, per sight group (`party`, or a character's token id
// under each character's own sight). The squares an event newly explores are STAMPED on it at commit (`sightStamp`),
// with the doors as the group then saw them, and `applyEvent` only adds them (`applyStamp`), so a replay never redoes
// the geometry and always gets the squares the table saw.
import { floorOf, hasFloors, sceneFloors, viewerFloors, wallsOnFloor } from '../state/floors.js';
import { blocksSight } from '../grid/walls.js';
import { EMPTY_EXTENT, FOG_LIMITS, footprintSquares, isRevealed, normaliseSpans, readDoorsSeen, readExplored } from '../state/fog.js';
import { outlineOf, ZONE_RULES } from '../state/zones.js';
import { indexWalls, lineClear, lineCellOf, litFrom, seenFrom, sightGrid, squareCentre, squareKey, squareSize, wallSquares } from './los.js';
import { summarise } from './vision.js';
import { weatherKey, withinWeather } from './weather.js';
import { storedLength } from '../state/stored.js';
import { exploredKey, exploredOn, fogFloor, floored, regroupExplored, viewerExplored } from '../state/floorFog.js';
export const SIGHT_MODES = ['party', 'character'];
export const defaultVision = () => ({ on: false, sight: 'party', extent: null });
export const VISION_KINDS = ['vision.set'];
const isSide = (v) => typeof v === 'number' && Number.isFinite(v) && v > 0 && v <= FOG_LIMITS.extent;
function parseExtent(v) {
    if (typeof v !== 'object' || v === null)
        return null;
    const { width, height } = v;
    return isSide(width) && isSide(height) ? { width, height } : null;
}
export function parseVisionBody(b) {
    if (b.kind !== 'vision.set')
        return null;
    const out = { kind: 'vision.set' };
    if (b.on !== undefined) {
        if (typeof b.on !== 'boolean')
            return null;
        out.on = b.on;
    }
    if (b.sight !== undefined) {
        if (!SIGHT_MODES.includes(b.sight))
            return null;
        out.sight = b.sight;
    }
    if (b.extent !== undefined) {
        const e = parseExtent(b.extent);
        if (!e)
            return null;
        out.extent = e;
    }
    if (b.clear !== undefined) {
        if (b.clear !== true)
            return null;
        out.clear = true;
    }
    return out.on === undefined && out.sight === undefined && !out.clear ? null : out;
}
export function normaliseVision(v) {
    if (typeof v !== 'object' || v === null)
        return defaultVision();
    const o = v;
    return { on: o.on === true, sight: o.sight === 'character' ? 'character' : 'party', extent: parseExtent(o.extent) };
}
/** A stored or wire event's stamp, read strictly enough to apply; null when it carries none. */
export function readStamp(b) {
    const out = {};
    if (b.explored !== undefined)
        out.explored = readExplored(b.explored);
    if (b.doorsSeen !== undefined)
        out.doorsSeen = readDoorsSeen(b.doorsSeen);
    return out.explored || out.doorsSeen ? out : null;
}
// ------------------------------------------------------------------------------------------------ spans and sets
export function spansOf(set, cols) {
    const keys = [...set].sort((a, b) => a - b);
    const out = [];
    for (const q of keys) {
        const i = q % cols;
        const j = Math.floor(q / cols);
        const last = out[out.length - 1];
        if (last && last.j === j && last.i1 === i - 1)
            last.i1 = i;
        else
            out.push({ j, i0: i, i1: i });
    }
    return out;
}
export function setOf(spans, g) {
    const out = new Set();
    for (const s of spans) {
        if (s.j >= g.rows)
            continue;
        for (let i = s.i0; i <= Math.min(s.i1, g.cols - 1); i += 1)
            out.add(s.j * g.cols + i);
    }
    return out;
}
const unionSpans = (lists) => normaliseSpans(lists.flat());
// ------------------------------------------------------------------------------------------------ the sight cache
/**
 * The lights that shine (issue 1145; design/30 §1.4, §4): each token's that is on, where the players see the token
 * stand, and each placed light that is on. A DM-hidden token's light lights nothing (§4: hidden means absent, and its
 * light is part of it), nor does one the DM is placing that the players have not been shown. None in a bright scene.
 */
function lightSources(state) {
    const out = [];
    const floored = hasFloors(state.scene);
    const floors = sceneFloors(state.scene);
    const on = (o) => (floored ? { floor: floorOf(o, floors) } : {});
    for (const o of Object.values(state.objects)) {
        if (!o.light?.on || o.hidden || (o.held && o.held.shown === null))
            continue;
        const at = o.held?.shown ?? { x: o.x, y: o.y };
        const f = on(o);
        out.push({ key: `${o.id}@${at.x},${at.y},${o.light.bright},${o.light.dim}${f.floor ? `^${f.floor}` : ''}`, at: { x: at.x, y: at.y }, bright: o.light.bright, dim: o.light.dim, ...f });
    }
    for (const l of state.lights ?? [])
        if (l.on) {
            const f = on(l);
            out.push({ key: `${l.id}@${l.x},${l.y},${l.bright},${l.dim}${f.floor ? `^${f.floor}` : ''}`, at: { x: l.x, y: l.y }, bright: l.bright, dim: l.dim, ...f });
        }
    return out.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
}
/**
 * In a dark scene, what an eye sees (§1.6; issue 1146): the squares lit FOR IT (its low-light vision's lights) and the
 * squares within its darkvision or blindsight range, whose centre is in its line of sight. Walls stop every kind.
 */
function seenLit(eye, lit, reach, idx) {
    const out = new Set();
    for (const q of lit.keys())
        if (lineClear(eye, squareCentre(idx.g, q), idx))
            out.add(q);
    for (const q of within(eye, reach, idx.g))
        if (!out.has(q) && lineClear(eye, squareCentre(idx.g, q), idx))
            out.add(q);
    return out;
}
/**
 * An eye's squares with the zones applied (issue 1149; §1.3, §1.5): magical darkness's squares are seen by nothing but
 * blindsight, which perceives every square within its range whose line is clear of walls and fog banks, darkness's
 * outline not counted; a never-revealed square is seen by no eye, so never explored.
 */
function zoned(squares, eye, blind, zs, blindIndex) {
    if (!zs.dark.size && !zs.hidden.size)
        return squares;
    for (const q of zs.dark)
        squares.delete(q);
    if (blind > 0 && zs.dark.size) {
        const bidx = blindIndex();
        for (const q of within(eye, blind, bidx.g))
            if (!squares.has(q) && lineClear(eye, squareCentre(bidx.g, q), bidx))
                squares.add(q);
    }
    for (const q of zs.hidden)
        squares.delete(q);
    return squares;
}
/** The squares whose centre lies within `range` squares of a point (in the board's average pitch, as a light measures). */
function within(at, range, g) {
    if (!(range > 0) || !g.cols || !g.rows)
        return [];
    const u = squareSize(g);
    const out = [];
    const i0 = lineCellOf(g.xs, at.x - range * u.x);
    const i1 = lineCellOf(g.xs, at.x + range * u.x);
    const j0 = lineCellOf(g.ys, at.y - range * u.y);
    const j1 = lineCellOf(g.ys, at.y + range * u.y);
    for (let j = j0; j <= j1; j += 1) {
        for (let i = i0; i <= i1; i += 1) {
            const q = squareKey(g, i, j);
            const c = squareCentre(g, q);
            if (Math.hypot((c.x - at.x) / u.x, (c.y - at.y) / u.y) <= range)
                out.push(q);
        }
    }
    return out;
}
/**
 * An eye's squares under the weather (issue 1163): only those within the limit of the eye's footprint — its nearest
 * square, counted by the system's diagonal rule, so 5 ft of fog shows every square touching the creature's space.
 * Darkvision is sight and is cut with it; blindsight perceives without sight, so within its range it still does.
 */
function weathered(squares, o, at, blind, w, state, g) {
    const fp = footprintSquares(state.grid, sightExtent(state), at, o.size);
    const felt = blind > 0 ? new Set(within(at, blind, g)) : null;
    for (const q of squares)
        if (!withinWeather(w, fp, q % g.cols, Math.floor(q / g.cols)) && !felt?.has(q))
            squares.delete(q);
    return squares;
}
const memo = new WeakMap();
const hints = new WeakMap();
export const visionOn = (state) => state?.vision?.on === true;
/** The map's size sight is worked out on: the vision's stamp, else the fog's, else the empty grid's. */
export const sightExtent = (state) => state.vision?.extent ?? state.fog?.extent ?? EMPTY_EXTENT;
/** The player characters whose sight counts: on the scene, not hidden, shown to the players (§2.1: only theirs is worked out). */
function eyeTokens(state) {
    return Object.values(state.objects).filter((o) => o.kind === 'token' && o.pc && !o.hidden && !(o.held && o.held.shown === null));
}
function keyOf(state) {
    const walls = (state.walls ?? []).filter(blocksSight).map((w) => `${w.a.x},${w.a.y},${w.b.x},${w.b.y}${w.floor ? `^${w.floor}` : ''}`).join(';');
    const floors = hasFloors(state.scene) ? `|${sceneFloors(state.scene).map((f) => f.id).join(',')}` : '';
    // Zones (issue 1149): their kinds and squares, never their labels — a label changes nobody's sight.
    const zones = (state.zones ?? []).map((z) => `${z.kind}:${z.spans.map((s) => `${s.j},${s.i0},${s.i1}`).join(' ')}`).join(';');
    return `${JSON.stringify(state.grid)}|${JSON.stringify(sightExtent(state))}|${walls}|${zones}${floors}`;
}
export function zoneSight(zones, g) {
    const outline = new Set();
    const dark = new Set();
    const hidden = new Set();
    for (const z of zones) {
        const rule = ZONE_RULES[z.kind];
        if (!rule)
            continue;
        const squares = setOf(z.spans, g);
        if (rule.outline)
            for (const q of squares)
                outline.add(q);
        if (rule.dark)
            for (const q of squares)
                dark.add(q);
        if (rule.unexplored)
            for (const q of squares)
                hidden.add(q);
    }
    const seeThrough = outline.size ? outlineOf(outline, g.cols, g.rows, g.xs, g.ys) : [];
    const walls = dark.size ? [...seeThrough, ...outlineOf(dark, g.cols, g.rows, g.xs, g.ys)] : seeThrough;
    return { walls, seeThrough, dark, hidden };
}
/**
 * Hint that `after` was reached from `before` by one event, so its sight is worked out from `before`'s (§2.1). A hint is
 * one step and is dropped once used (issue 1348): a hint holds its `before` alive, so hints left in place chained every
 * state a table ever had to its newest one — the whole history in memory while the table was played. A `before`
 * still waiting on its own hint gives it up here, so no chain is ever two long; its sight, if asked, is worked out whole.
 */
export function advanceSight(before, after) {
    if (before === after || memo.has(after))
        return;
    if (!memo.has(before))
        hints.delete(before);
    hints.set(after, before);
}
/** Whether a state still holds a hint to the one before it (issue 1348; tests). */
export const hasSightHint = (state) => hints.has(state);
/** The state's sight, or null with vision off. Memoised per state object. */
export function sightOf(state) {
    if (memo.has(state))
        return memo.get(state);
    if (!visionOn(state)) {
        memo.set(state, null);
        hints.delete(state);
        return null;
    }
    const prevState = hints.get(state);
    hints.delete(state); // used now: the state no longer holds the one before it (issue 1348)
    const prev = prevState && memo.has(prevState) ? memo.get(prevState) : null;
    const key = keyOf(state);
    const same = prev !== null && prev.key === key;
    const g = same ? prev.g : sightGrid(state.grid, sightExtent(state));
    // Zones (issue 1149): fog banks' and magical darkness's outlines are walls in the one index every eye and light uses;
    // blindsight perceives through magical darkness (§1.5), so it walks an index without darkness's outline, made only
    // when some darkness and some blindsight are on the scene.
    const zs = same ? prev.zones : zoneSight(state.zones ?? [], g);
    const idx = same ? prev.idx : indexWalls(zs.walls.length ? [...(state.walls ?? []), ...zs.walls] : state.walls ?? [], g);
    // Floors (qa-runs#1160 slice B): each floor's walls stop sight and light on that floor alone; an eye and a light use
    // their own floor's index. Without floors, the one index.
    const floored = hasFloors(state.scene);
    const floors = sceneFloors(state.scene);
    const idxs = same ? prev.idxs : new Map();
    const idxOn = (floor) => {
        if (!floored || floor === undefined)
            return idx;
        const have = idxs.get(floor);
        if (have)
            return have;
        const mine = wallsOnFloor(state.walls ?? [], floor, state.scene);
        const made = indexWalls(zs.walls.length ? [...mine, ...zs.walls] : mine, g);
        idxs.set(floor, made);
        return made;
    };
    let blindIdx = same ? prev.blindIdx : null;
    const blindIndex = () => (blindIdx ??= zs.walls.length === zs.seeThrough.length ? idx : indexWalls([...(state.walls ?? []), ...zs.seeThrough], g));
    // No light shines inside magical darkness (§1.3): its squares are taken out of every light's.
    const unlit = (m) => { if (zs.dark.size)
        for (const q of zs.dark)
            m.delete(q); return m; };
    // Light (issue 1145; design/30 §1.6, §2.1): a square is seen when it is in the eye's line AND lit. In a bright or a
    // dim scene every square is lit, so lights change only which seen squares are bright, never what is seen: an eye is
    // carried across a light's change. In the dark only lights light anything, so a light added, changed, moved, switched
    // or removed recomputes every eye; each light's own squares are carried while it and the walls are unchanged.
    const level = state.lightLevel ?? 'bright';
    const sources = level === 'bright' ? [] : lightSources(state);
    const lightKey = level === 'dark' ? `dark|${sources.map((l) => l.key).join(';')}` : level;
    const litBy = new Map();
    let lit = null;
    // Vision (issue 1146; §1.5): low-light vision counts every light's radii `multiplier` times larger for that eye, so
    // each factor an eye has gets its own lit squares, each light's carried by its key and the factor like the plain ones.
    // With floors (qa-runs#1160 slice B), each floor's lights light that floor alone, through its own walls.
    const litFor = new Map();
    const forFactor = (m, floor) => {
        const fk = `${floored ? floor ?? '' : ''}|${m}`;
        const have = litFor.get(fk);
        if (have)
            return have;
        const merged = new Map();
        for (const l of sources) {
            if (floored && l.floor !== floor)
                continue;
            const k = m === 1 ? l.key : `${l.key}×${m}`;
            const squares = (same ? prev.litBy.get(k) : undefined) ?? unlit(litFrom(l.at, l.bright * m, l.dim * m, idxOn(l.floor)));
            litBy.set(k, squares);
            for (const [q, bright] of squares)
                if (bright)
                    merged.set(q, true);
                else if (!merged.has(q))
                    merged.set(q, false);
        }
        litFor.set(fk, merged);
        return merged;
    };
    if (level !== 'bright') {
        if (!floored)
            lit = forFactor(1);
        else {
            lit = new Map();
            for (const f of floors)
                for (const [q, b] of forFactor(1, f.id))
                    if (b)
                        lit.set(q, true);
                    else if (!lit.has(q))
                        lit.set(q, false);
        }
    }
    // Weather (issue 1163): a limit cuts every eye, so a change of it recomputes them all; its name changes no one's sight.
    const weather = state.weather ?? null;
    const wKey = weatherKey(weather);
    const carry = same && prev.lightKey === lightKey && prev.weatherKey === wKey;
    const eyes = new Map();
    for (const o of eyeTokens(state)) {
        const at = o.held?.shown ?? { x: o.x, y: o.y };
        const senses = summarise(o.vision);
        const floor = floored ? floorOf(o, floors) : undefined;
        const had = carry ? prev.eyes.get(o.id) : undefined;
        const kept = had && had.x === at.x && had.y === at.y && had.senses.key === senses.key && had.floor === floor;
        const eyeIdx = idxOn(floor);
        if (lit)
            forFactor(senses.multiplier, floor);
        const seen = () => zoned(level === 'dark' ? seenLit(at, forFactor(senses.multiplier, floor), Math.max(senses.dark, senses.all), eyeIdx) : seenFrom(at, eyeIdx), at, senses.all, zs, blindIndex);
        const squares = kept ? had.squares : wKey ? weathered(seen(), o, at, senses.all, weather, state, g) : seen();
        eyes.set(o.id, { id: o.id, x: at.x, y: at.y, owners: [...o.owners], squares, senses, ...(floor ? { floor } : {}) });
    }
    const out = { g, key, idx, zones: zs, blindIdx, eyes, level, lightKey, weatherKey: wKey, litBy, lit, litFor, idxs, groups: new Map(), viewers: new Map() };
    memo.set(state, out);
    return out;
}
/** The squares a group sees now: a character's own, or (`party`) every character's combined. */
function groupSeen(s, group) {
    const have = s.groups.get(group);
    if (have)
        return have;
    let out;
    if (group === 'party') {
        out = new Set();
        for (const e of s.eyes.values())
            for (const q of e.squares)
                out.add(q);
    }
    else
        out = s.eyes.get(group)?.squares ?? new Set();
    s.groups.set(group, out);
    return out;
}
/** The groups the current setting explores for: the party, or each character on the scene. */
function activeGroups(state, s) {
    return state.vision.sight === 'party' ? ['party'] : [...s.eyes.keys()];
}
function viewerOf(state, s, viewerId) {
    const have = s.viewers.get(viewerId);
    if (have)
        return have;
    let groups = ['party'];
    if (state.vision.sight === 'character') {
        const own = [...s.eyes.values()].filter((e) => e.owners.includes(viewerId)).map((e) => e.id);
        if (own.length)
            groups = own;
    }
    let seen;
    if (groups.length === 1)
        seen = groupSeen(s, groups[0]);
    else {
        seen = new Set();
        for (const g of groups)
            for (const q of groupSeen(s, g))
                seen.add(q);
    }
    const out = { groups, seen };
    s.viewers.set(viewerId, out);
    return out;
}
/** The squares this player sees now, or null with vision off (the DM sees everything). */
export function viewerSeen(state, viewerId) {
    const s = sightOf(state);
    return s ? viewerOf(state, s, viewerId).seen : null;
}
/** A group's explored squares on a floor (qa-runs#1160; the ground floor, or the only one, is null); under each character's own sight, the party is everyone's there. */
function groupExplored(state, group, floor = null) {
    return exploredOn(state.fog?.explored, group, floor, state.vision.sight === 'character');
}
/** The squares a group sees now on each floor its eyes stand on (qa-runs#1160): with no floors, one entry, null. */
function groupSeenByFloor(state, s, group) {
    if (!floored(state))
        return new Map([[null, groupSeen(s, group)]]);
    const out = new Map();
    const eyes = group === 'party' ? [...s.eyes.values()] : [s.eyes.get(group)].filter((e) => !!e);
    for (const e of eyes) {
        const f = fogFloor(state, e);
        let set = out.get(f);
        if (!set) {
            set = new Set();
            out.set(f, set);
        }
        for (const q of e.squares)
            set.add(q);
    }
    return out;
}
// ------------------------------------------------------------------------------------------------ what a player is sent
const footprints = new WeakMap();
function footprintOf(o, state) {
    const extent = sightExtent(state);
    const had = footprints.get(o);
    if (had && had.grid === state.grid && had.extent.width === extent.width && had.extent.height === extent.height)
        return had.box;
    const box = footprintSquares(state.grid, extent, o.held?.shown ?? { x: o.x, y: o.y }, o.size);
    footprints.set(o, { grid: state.grid, extent, box });
    return box;
}
/**
 * Whether a player's sight reaches a thing (§1.6: any square of its footprint seen). Each thing's footprint is worked
 * out once per state, not once per viewer (§2.3); the viewer's sight is a set of squares.
 */
export function sightReaches(o, viewerId, state) {
    const s = sightOf(state);
    if (!s)
        return true;
    const b = footprintOf(o, state);
    const reaches = (seen) => {
        for (let j = b.j0; j <= Math.min(b.j1, s.g.rows - 1); j += 1)
            for (let i = b.i0; i <= Math.min(b.i1, s.g.cols - 1); i += 1)
                if (seen.has(j * s.g.cols + i))
                    return true;
        return false;
    };
    // No particular player (an attack's or a save's record, stamped once for every player, `seen`): under each
    // character's own sight, only what EVERY character sees, so the record tells no player of a thing their own
    // character does not see. Under the party's combined sight, the party's.
    if (!viewerId && state.vision.sight === 'character')
        return s.eyes.size > 0 && [...s.eyes.values()].every((e) => reaches(e.squares));
    return reaches(viewerOf(state, s, viewerId).seen);
}
/**
 * How a player's seen squares are lit (issues 1145, 1146): per square, the best any of their eyes that sees it has —
 * bright, dim, or (in the dark) grey, seen with no light. Each eye reads the lights through its own low-light factor.
 * Null in a bright scene.
 */
function viewerShade(state, s, v) {
    if (!s.lit)
        return null;
    if (v.shade)
        return v.shade;
    const eyes = v.groups.length === 1 && v.groups[0] === 'party' ? [...s.eyes.values()] : v.groups.map((g) => s.eyes.get(g)).filter((e) => !!e);
    const dim = new Set();
    const grey = new Set();
    for (const q of v.seen) {
        let best = -1; // 0 grey, 1 dim, 2 bright
        for (const e of eyes) {
            if (!e.squares.has(q))
                continue;
            const lit = (s.litFor.get(`${e.floor ?? ''}|${e.senses.multiplier}`) ?? s.lit).get(q);
            best = Math.max(best, lit === true ? 2 : lit === false || s.level === 'dim' ? 1 : 0);
            if (best === 2)
                break;
        }
        if (best === 0)
            grey.add(q);
        else if (best === 1)
            dim.add(q);
    }
    v.shade = { dim, grey };
    return v.shade;
}
export function playerSight(state, viewerId) {
    const s = sightOf(state);
    if (!s)
        return null;
    const v = viewerOf(state, s, viewerId);
    const shade = viewerShade(state, s, v);
    // Explored per floor (qa-runs#1160): the ground floor's as `explored`, each other floor they hold in `exploredOn`.
    const ex = viewerExplored(state, v.groups, viewerId, state.vision.sight === 'character');
    return {
        explored: ex.explored, ...(ex.exploredOn ? { exploredOn: ex.exploredOn } : {}), seen: spansOf(v.seen, s.g.cols),
        ...(shade ? { dim: spansOf(shade.dim, s.g.cols) } : {}), ...(shade?.grey.size ? { grey: spansOf(shade.grey, s.g.cols) } : {}),
    };
}
/**
 * The placed lights a player is sent (issue 1145; design/30 §4): each that is on and whose source square they see now,
 * its point and radii, to draw its glow — no id, no switch. Any other light reaches them only as the squares it lights
 * in their sight. A token's light rides on the token, when the token is sent. Null with vision off or the scene bright.
 */
export function playerLights(state, viewerId) {
    const s = sightOf(state);
    if (!s || !s.lit)
        return null;
    const seen = viewerOf(state, s, viewerId).seen;
    const out = [];
    // Floors (qa-runs#1160 slice B): a light lights its own floor; a player is sent those on their floors alone.
    const floors = sceneFloors(state.scene);
    const mine = hasFloors(state.scene) ? viewerFloors(state.objects, viewerId, floors) : null;
    for (const l of state.lights ?? []) {
        if (!l.on)
            continue;
        if (mine && !mine.has(floorOf(l, floors)))
            continue;
        const i = lineCellOfBoard(s.g.xs, l.x);
        const j = lineCellOfBoard(s.g.ys, l.y);
        if (i !== null && j !== null && seen.has(j * s.g.cols + i))
            out.push({ x: l.x, y: l.y, bright: l.bright, dim: l.dim });
    }
    return out;
}
/** The cell a coordinate falls in, or null off the board. */
function lineCellOfBoard(lines, v) {
    if (lines.length < 2 || v < lines[0] || v > lines[lines.length - 1])
        return null;
    let lo = 0;
    let hi = lines.length - 2;
    while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (v >= lines[mid])
            lo = mid;
        else
            hi = mid - 1;
    }
    return lo;
}
/**
 * The doors a player is sent with vision off (qa-runs#1202, Tom's decision of 2026-11-01): every door on a floor of
 * theirs, ends and state only, so its marker is there to click; plain walls stay unsent. A secret door is a wall with no
 * door on it, so nothing of one comes here. A door the manual fog wholly covers is not sent, as a thing there is not,
 * and while the DM places the table none is: a door drawn then has not been shown to anyone (issue 811). None, no key.
 */
function playerDoors(state, viewerId) {
    if (state.placing)
        return null;
    const doors = (state.walls ?? []).filter((w) => w.door);
    if (!doors.length)
        return null;
    const floors = sceneFloors(state.scene);
    const mine = hasFloors(state.scene) ? viewerFloors(state.objects, viewerId, floors) : null;
    const fog = state.fog?.on ? state.fog : null;
    const g = fog ? sightGrid(state.grid, fog.extent ?? EMPTY_EXTENT) : null;
    const out = [];
    for (const w of doors) {
        if (mine && !mine.has(floorOf(w, floors)))
            continue;
        if (fog && g && !wallSquares(w, g).some((q) => isRevealed(fog.revealed, q % g.cols, Math.floor(q / g.cols))))
            continue;
        out.push({ a: { x: w.a.x, y: w.a.y }, b: { x: w.b.x, y: w.b.y }, ...(mine ? { floor: floorOf(w, floors) } : {}), door: { open: w.door.open } });
    }
    return out.length ? out : null;
}
/**
 * The walls a player is sent (§4): each sight-blocking wall that borders or crosses one of their explored squares, its
 * ends only; each door that does, as their groups last saw it (shut, if none of them has seen it). A window (a wall
 * that does not block sight) is not sent; nothing of a wall wholly in unexplored squares is. With vision off, only the
 * doors (`playerDoors`).
 */
export function playerWalls(state, viewerId) {
    const s = sightOf(state);
    if (!s)
        return playerDoors(state, viewerId);
    const v = viewerOf(state, s, viewerId);
    // Each wall against its own floor's explored squares (qa-runs#1160); with no floors, the one set.
    const exploredSets = new Map();
    const exploredSet = (f) => {
        let set = exploredSets.get(f);
        if (!set) {
            set = setOf(unionSpans(v.groups.map((g) => groupExplored(state, g, f))), s.g);
            exploredSets.set(f, set);
        }
        return set;
    };
    const seenBy = state.fog?.doorsSeen ?? {};
    const out = [];
    // Floors (qa-runs#1160): only the walls of the floors their tokens are on.
    const floors = sceneFloors(state.scene);
    const mine = hasFloors(state.scene) ? viewerFloors(state.objects, viewerId, floors) : null;
    for (const w of state.walls ?? []) {
        if (!w.door && !w.sight)
            continue;
        if (mine && !mine.has(floorOf(w, floors)))
            continue;
        const explored = exploredSet(fogFloor(state, w));
        if (!wallSquares(w, s.g).some((q) => explored.has(q)))
            continue;
        const ends = { a: { x: w.a.x, y: w.a.y }, b: { x: w.b.x, y: w.b.y }, ...(mine ? { floor: floorOf(w, floors) } : {}) };
        if (!w.door) {
            out.push(ends);
            continue;
        }
        const groups = v.groups.length === 1 && v.groups[0] === 'party' && state.vision.sight === 'character' ? Object.keys(seenBy) : v.groups;
        const open = groups.some((g) => seenBy[g]?.[w.id] === true);
        out.push({ ...ends, door: { open } });
    }
    return out;
}
// ------------------------------------------------------------------------------------------------ exploring
/**
 * The most spans one event's stamp carries, over all its groups (the 2026-10-10 review's finding 1). A span's stored
 * text is at most 37 characters (`{"j": 2000, "i0": 2000, "i1": 2000}, `), so 1,200 of them keep any event far under the
 * database's 65,536. A big map's first sight is explored over several events, each adding the next rows in order.
 */
export const STAMP_SPANS_MAX = 1200;
/**
 * The most stored characters (`storedLength`) one event's stamp may weigh, squares and doors together (the second
 * 2026-10-10 review's finding 1): each seen door adds about 45 (`"<uuid>": false, `), and 120 doors seen by five
 * characters' groups passed the 65,536 alone. 48,000 leaves the event's own body its room.
 */
export const STAMP_CHARS_MAX = 48_000;
/** The most a span adds to a stamp's stored text (see `STAMP_SPANS_MAX`). */
const SPAN_CHARS = 37;
/** What a group's key adds to one of a stamp's records: `"<group>": {}, `. */
const groupChars = (g) => storedLength(g) + 6;
/**
 * What the state's sight newly explores (§3.2), per active group: squares seen and not yet explored, and doors seen in
 * a state the group has not recorded. Null when nothing is new, or vision is off. A group at the cap stops adding (§3.5).
 * At most `STAMP_SPANS_MAX` spans in all, the first rows first, and never more than the group's room under the cap, so
 * the union `applyStamp` makes always fits and explored grows to the cap and stops there; later events add the rest.
 * Squares and doors together weigh at most `STAMP_CHARS_MAX` stored characters: the doors past it (walls' order) are
 * left for later events, as squares are, since each later stamp adds only what its group has not recorded.
 */
export function sightStamp(state) {
    const s = sightOf(state);
    if (!s)
        return null;
    let budget = STAMP_SPANS_MAX;
    // The stamp's stored weight so far: the two records' keys and braces, then each group's entries.
    let chars = 40;
    const explored = {};
    const doorsSeen = {};
    const doors = (state.walls ?? []).filter((w) => w.door !== undefined);
    for (const g of activeGroups(state, s)) {
        const seen = groupSeen(s, g);
        if (!seen.size)
            continue;
        // Each floor the group's eyes stand on explores that floor's record (qa-runs#1160); with no floors, the group's own.
        for (const [floor, onFloor] of groupSeenByFloor(state, s, g)) {
            const key = exploredKey(g, floor);
            const had = state.fog?.explored?.[key] ?? [];
            const room = Math.min(budget, FOG_LIMITS.spans - had.length, Math.floor((STAMP_CHARS_MAX - chars - groupChars(key)) / SPAN_CHARS));
            if (room <= 0)
                continue;
            const known = setOf(had, s.g);
            const fresh = new Set();
            for (const q of onFloor)
                if (!known.has(q))
                    fresh.add(q);
            // Fresh squares are none of the known ones, so the union has at most `had.length + room` spans.
            const spans = fresh.size ? spansOf(fresh, s.g.cols).slice(0, room) : [];
            if (spans.length) {
                explored[key] = spans;
                budget -= spans.length;
                chars += groupChars(key) + spans.length * SPAN_CHARS;
            }
        }
        const saw = state.fog?.doorsSeen?.[g] ?? {};
        for (const d of doors) {
            if (saw[d.id] === d.door.open)
                continue;
            if (!wallSquares(d, s.g).some((q) => seen.has(q)))
                continue;
            // `"<id>": false, `, and the group's key with the first.
            const cost = storedLength(d.id) + 9 + (doorsSeen[g] ? 0 : groupChars(g));
            if (chars + cost > STAMP_CHARS_MAX)
                break;
            chars += cost;
            (doorsSeen[g] ??= {})[d.id] = d.door.open;
        }
    }
    const out = {};
    if (Object.keys(explored).length)
        out.explored = explored;
    if (Object.keys(doorsSeen).length)
        out.doorsSeen = doorsSeen;
    return out.explored || out.doorsSeen ? out : null;
}
/** Add a stamp to a state (what `applyEvent` does with an event's stamp): a union, held to the cap. */
export function applyStamp(state, stamp) {
    if (!stamp || (!stamp.explored && !stamp.doorsSeen))
        return state;
    const fog = state.fog;
    const explored = { ...(fog.explored ?? {}) };
    for (const [g, spans] of Object.entries(stamp.explored ?? {})) {
        const next = normaliseSpans([...(explored[g] ?? []), ...spans]);
        explored[g] = next.length > FOG_LIMITS.spans ? (explored[g] ?? []) : next;
    }
    const doorsSeen = { ...(fog.doorsSeen ?? {}) };
    for (const [g, d] of Object.entries(stamp.doorsSeen ?? {}))
        doorsSeen[g] = { ...(doorsSeen[g] ?? {}), ...d };
    return { ...state, fog: { ...fog, explored, doorsSeen } };
}
/**
 * The scene's vision after the DM's switch or setting (§3.3). Switching to each character's own sight, every character
 * on the scene starts with the party's explored squares and doors; switching back, the party's are the union of every
 * character's. A new map (`clear`) forgets every group's.
 */
export function reduceVision(state, body) {
    const was = state.vision ?? defaultVision();
    const vision = { on: body.on ?? was.on, sight: body.sight ?? was.sight, extent: body.extent ?? was.extent };
    let fog = state.fog;
    if (body.clear)
        fog = { ...fog, explored: {}, doorsSeen: {} };
    else if (vision.sight !== was.sight) {
        // Explored squares regroup floor by floor (qa-runs#1160; `regroupExplored`), doors as before.
        const explored = regroupExplored(fog.explored ?? {}, vision.sight === 'character', eyeTokens(state).map((o) => o.id));
        const doorsSeen = { ...(fog.doorsSeen ?? {}) };
        if (vision.sight === 'character') {
            for (const o of eyeTokens(state))
                doorsSeen[o.id] = { ...(doorsSeen.party ?? {}), ...(doorsSeen[o.id] ?? {}) };
        }
        else {
            const all = {};
            for (const d of Object.values(doorsSeen))
                Object.assign(all, d);
            doorsSeen.party = { ...all, ...(doorsSeen.party ?? {}) };
        }
        for (const g of Object.keys(explored))
            if (explored[g].length > FOG_LIMITS.spans)
                explored[g] = explored[g].slice(0, FOG_LIMITS.spans);
        fog = { ...fog, explored, doorsSeen };
    }
    return { ...state, vision, fog };
}
//# sourceMappingURL=sight.js.map